// Per-organization external telephony database connections.
//
// Call records for some tenants live on the SIP servers that handled the call
// rather than in cpass. This is where those connections are configured and,
// crucially, verified — Test Connection is the only way to tell whether the
// network path, the credentials and the database name are all actually right.
import React, { useEffect, useState } from 'react';
import {
  Database, Plus, Pencil, Trash2, PlugZap, Check, X, AlertTriangle, Search, Loader2,
} from 'lucide-react';
import {
  dataSourcesService, DataSource, TestResult, DiscoveredBusiness, PatternPreview,
} from 'api/services/dataSources.service';

const BLANK: DataSource = {
  name: '', sourceKind: 'LANDLINE', host: '', port: 3306,
  databaseName: '', username: '', password: '',
  businessIdPatterns: '', enabled: true, queryTimeoutSeconds: 15,
};

const CallDataSources: React.FC = () => {
  const [rows, setRows] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<DataSource | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, TestResult>>({});
  const [inspecting, setInspecting] = useState<string | null>(null);
  const [businesses, setBusinesses] = useState<DiscoveredBusiness[] | null>(null);
  const [preview, setPreview] = useState<PatternPreview | null>(null);

  const load = async () => {
    setLoading(true);
    const r = await dataSourcesService.list();
    if (r.success) setRows(r.data || []); else setError(r.error || 'Failed to load');
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!editing) return;
    setSaving(true); setError(null);
    const r = editing.id
      ? await dataSourcesService.update(editing.id, editing)
      : await dataSourcesService.create(editing);
    setSaving(false);
    if (r.success) { setEditing(null); load(); } else setError(r.error || 'Save failed');
  };

  const remove = async (row: DataSource) => {
    if (!row.id) return;
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete "${row.name}"? The stored credential is removed with it.`)) return;
    const r = await dataSourcesService.remove(row.id);
    if (r.success) load(); else setError(r.error || 'Delete failed');
  };

  const test = async (row: DataSource) => {
    if (!row.id) return;
    setTesting(row.id);
    const r = await dataSourcesService.test(row.id);
    setTesting(null);
    if (r.success && r.data) {
      setResults(p => ({ ...p, [row.id!]: r.data! }));
      load();   // refresh the stored last-tested state
    } else {
      setResults(p => ({ ...p, [row.id!]: { ok: false, message: r.error || 'Test failed' } }));
    }
  };

  const inspect = async (row: DataSource) => {
    if (!row.id) return;
    setInspecting(row.id); setBusinesses(null); setPreview(null);
    const [b, p] = await Promise.all([
      dataSourcesService.businesses(row.id, 50),
      dataSourcesService.patternPreview(row.id),
    ]);
    if (b.success) setBusinesses(b.data || []);
    if (p.success) setPreview(p.data || null);
  };

  const input = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent';
  const lbl = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
            <Database className="w-5 h-5" style={{ color: 'var(--accent)' }} />
            Call data sources
          </h1>
          <p className="text-[13px] mt-1" style={{ color: 'var(--text-muted)' }}>
            External telephony databases this organization's call data is read from.
            Reads are live — nothing is copied into cpass.
          </p>
        </div>
        <button className="tl-btn tl-btn-primary" onClick={() => setEditing({ ...BLANK })}>
          <Plus className="w-4 h-4" /> Add source
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-3 py-2">{error}</div>
      )}

      {loading ? (
        <div className="tl-card flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="tl-card text-center py-10">
          <Database className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="text-sm font-medium text-gray-900 dark:text-white">No sources configured</p>
          <p className="text-[13px] mt-1" style={{ color: 'var(--text-muted)' }}>
            Add the landline and mobile switches this organization's calls run through.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(row => {
            const res = row.id ? results[row.id] : undefined;
            const ok = res ? res.ok : row.lastTestOk;
            const msg = res ? res.message : row.lastTestMessage;
            return (
              <div key={row.id} className="tl-card">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 dark:text-white">{row.name}</span>
                      <span className="tl-pill tl-pill-coral">{row.sourceKind}</span>
                      {!row.enabled && <span className="tl-pill">Disabled</span>}
                      {ok === true && <span className="tl-pill tl-pill-success"><Check className="w-3 h-3" /> Connected</span>}
                      {ok === false && <span className="tl-pill tl-pill-danger"><X className="w-3 h-3" /> Unreachable</span>}
                    </div>
                    <div className="text-[12.5px] mt-1 font-mono" style={{ color: 'var(--text-muted)' }}>
                      {row.username}@{row.host}:{row.port}/{row.databaseName}
                    </div>
                    <div className="text-[12px] mt-1" style={{ color: 'var(--text-muted)' }}>
                      Patterns: <span className="font-mono">{row.businessIdPatterns || '— none set, this source returns nothing —'}</span>
                    </div>
                    {msg && (
                      <div className="text-[12px] mt-1.5" style={{ color: ok ? 'var(--success-text)' : 'var(--danger-text)' }}>
                        {msg}
                      </div>
                    )}
                    {res && !res.ok && res.hint && (
                      <div className="text-[12px] mt-1.5 px-2.5 py-1.5 rounded-lg flex items-start gap-1.5"
                           style={{ background: 'var(--amber-tint)', color: 'var(--amber-label)' }}>
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>{res.hint}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={() => test(row)} disabled={testing === row.id}>
                      {testing === row.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlugZap className="w-3.5 h-3.5" />}
                      Test
                    </button>
                    <button className="tl-btn tl-btn-neutral tl-btn-sm" onClick={() => inspect(row)}>
                      <Search className="w-3.5 h-3.5" /> Inspect
                    </button>
                    <button className="tl-icon-btn" title="Edit" onClick={() => setEditing({ ...row, password: '' })}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button className="tl-icon-btn" title="Delete" onClick={() => remove(row)}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {inspecting === row.id && (
                  <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--divider)' }}>
                    {preview && (
                      <div className="mb-3">
                        <div className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                          Your patterns match {preview.matched.toLocaleString()} calls
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {preview.businessIds.map(b => (
                            <span key={b.businessId} className="tl-pill tl-pill-success">
                              {b.businessId} · {b.calls.toLocaleString()}
                            </span>
                          ))}
                          {preview.businessIds.length === 0 && (
                            <span className="text-[12px]" style={{ color: 'var(--danger-text)' }}>
                              Nothing matched — check the patterns against the list below.
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {businesses && (
                      <>
                        <div className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                          All business IDs on this source ({businesses.length})
                        </div>
                        <div className="max-h-48 overflow-y-auto flex flex-wrap gap-1.5">
                          {businesses.map(b => (
                            <span key={b.businessId} className="tl-pill" title={`newest: ${b.newest}`}>
                              {b.businessId} · {b.calls.toLocaleString()}
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                    {!businesses && !preview && (
                      <div className="text-[13px] flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
                        <Loader2 className="w-4 h-4 animate-spin" /> Reading from source…
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'var(--scrim)' }}>
          <div className="tl-card w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              {editing.id ? 'Edit source' : 'Add source'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={lbl}>Name</label>
                <input className={input} value={editing.name}
                       onChange={e => setEditing({ ...editing, name: e.target.value })}
                       placeholder="Tata Landline" />
              </div>
              <div>
                <label className={lbl}>Kind</label>
                <select className={input} value={editing.sourceKind}
                        onChange={e => setEditing({ ...editing, sourceKind: e.target.value as any })}>
                  <option value="LANDLINE">Landline</option>
                  <option value="MOBILE">Mobile</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div className="sm:col-span-2 grid grid-cols-[1fr_120px] gap-3">
                <div>
                  <label className={lbl}>Host</label>
                  <input className={input} value={editing.host}
                         onChange={e => setEditing({ ...editing, host: e.target.value })}
                         placeholder="10.54.157.133" />
                </div>
                <div>
                  <label className={lbl}>Port</label>
                  <input type="number" className={input} value={editing.port}
                         onChange={e => setEditing({ ...editing, port: parseInt(e.target.value || '3306') })} />
                </div>
              </div>
              <div>
                <label className={lbl}>Database</label>
                <input className={input} value={editing.databaseName}
                       onChange={e => setEditing({ ...editing, databaseName: e.target.value })}
                       placeholder="teeoff-ivr-gateway" />
              </div>
              <div>
                <label className={lbl}>Username</label>
                <input className={input} value={editing.username}
                       onChange={e => setEditing({ ...editing, username: e.target.value })}
                       placeholder="cpass_ro" />
              </div>
              <div className="sm:col-span-2">
                <label className={lbl}>
                  Password {editing.id && <span style={{ color: 'var(--text-muted)' }}>— leave blank to keep the current one</span>}
                </label>
                <input type="password" className={input} value={editing.password || ''}
                       onChange={e => setEditing({ ...editing, password: e.target.value })}
                       autoComplete="new-password" />
                <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  Encrypted at rest and never returned by the API. Use a read-only database user.
                </p>
              </div>
              <div className="sm:col-span-2">
                <label className={lbl}>Business ID patterns</label>
                <input className={input + ' font-mono'} value={editing.businessIdPatterns || ''}
                       onChange={e => setEditing({ ...editing, businessIdPatterns: e.target.value })}
                       placeholder="catchMyCall%,CMC_%" />
                <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  Comma-separated SQL LIKE patterns. The switch stores a free-text business ID per IVR app,
                  so one tenant usually appears under several. Note <span className="font-mono">_</span> is a
                  single-character wildcard — prefer a prefix with a trailing <span className="font-mono">%</span>.
                  Use Inspect after saving to confirm what matched.
                </p>
              </div>
              <div>
                <label className={lbl}>Query timeout (seconds)</label>
                <input type="number" min={1} max={120} className={input} value={editing.queryTimeoutSeconds}
                       onChange={e => setEditing({ ...editing, queryTimeoutSeconds: parseInt(e.target.value || '15') })} />
              </div>
              <div className="flex items-end">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 pb-2">
                  <input type="checkbox" checked={!!editing.enabled}
                         onChange={e => setEditing({ ...editing, enabled: e.target.checked })} />
                  Enabled
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button className="tl-btn tl-btn-neutral" onClick={() => setEditing(null)}>Cancel</button>
              <button className="tl-btn tl-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallDataSources;
